import { useState } from 'react'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import Tooltip from '@mui/material/Tooltip'
import { heapEvent } from '@equipmentshare/heaper'
import { FulfillmentOrderStatus } from '../../../graphql'
import { useFulfillmentOrderCancellation } from '../../../hooks/useFulfillmentOrderCancellation'
import { useShowError, useShowSuccess } from '../../../hooks/useToast'

type Props = {
	fulfillmentOrderId: string
}

export default function CancelFulfillmentOrderButton( { fulfillmentOrderId }: Props ) {
	const [ dialogOpen, setDialogOpen ] = useState( false )
	const showSuccess = useShowSuccess()
	const showError = useShowError()

	const { status, phase, tookTooLong, isInProgress, submit } = useFulfillmentOrderCancellation( {
		fulfillmentOrderId,
		enabled: true,
		onSucceeded: () => showSuccess( `cancelFo-${fulfillmentOrderId}`, 'Fulfillment order cancelled.' ),
		onFailed: ( message ) => showError( `cancelFo-${fulfillmentOrderId}`, message ?? 'Cancellation failed.' ),
	} )

	if (
		status === FulfillmentOrderStatus.Cancelled ||
		status === FulfillmentOrderStatus.Completed ||
		phase === 'succeeded'
	) {
		return null
	}

	if ( isInProgress ) {
		return tookTooLong ? (
			<Alert severity="warning" sx={{ py: 0, fontSize: 13 }}>
				Cancellation is taking longer than expected…
			</Alert>
		) : null
	}

	const handleConfirm = () => {
		setDialogOpen( false )
		heapEvent( 'Inventory - DC Admin - FO Details - Cancel FO', { fulfillment_order_id: fulfillmentOrderId } )
		submit()
	}

	return (
		<>
			<Tooltip title="Request cancellation of this fulfillment order">
				<span>
					<Button
						variant="outlined"
						color="error"
						size="small"
						disabled={phase === 'requesting'}
						startIcon={phase === 'requesting' ? <CircularProgress size={14} color="inherit" /> : undefined}
						onClick={() => setDialogOpen( true )}
					>
						Cancel FO
					</Button>
				</span>
			</Tooltip>

			<Dialog open={dialogOpen} onClose={() => setDialogOpen( false )} maxWidth="xs">
				<DialogTitle>Cancel Fulfillment Order?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						This will request cancellation from Numina. The order will be updated once the cancellation is processed.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialogOpen( false )}>Back</Button>
					<Button onClick={handleConfirm} color="error" variant="contained" autoFocus>
						Confirm Cancellation
					</Button>
				</DialogActions>
			</Dialog>
		</>
	)
}
